import { startAppBootStep } from 'hybridspace-telemetry/lib/logAppBootStep';
import { logGreyError } from 'owa-analytics';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { addDays, getISOString, isAfter, now, userDate } from 'owa-datetime';
import { PLACES_SETTINGS_TTL_IN_DAYS } from './cache/database';
import { getOfflinePlacesSettings } from './cache/getOfflinePlacesSettings';
import { putOfflinePlacesSettings } from './cache/putOfflinePlacesSettings';
import { defaultPlacesSettings } from './fetchPlacesSettings';
import retryFetchPlacesSettings from './retryFetchPlacesSettings';
import {
    getPlacesSettings,
    hasLoaded,
    resetPlacesSettings,
    setPlacesSettings,
    setPlacesSettingsFailed,
} from './store';

import type { PlacesTenantSettingComplete } from './types/PlacesTenantSettingCompleteCallback';
import type { MailboxInfo } from 'owa-client-types';
import type { PlacesSettings } from './types/PlacesSettings';
import type { TraceErrorObject } from 'owa-analytics-types';

export default function loadPlacesSettings(
    mailboxInfo: MailboxInfo,
    shouldUseMainThread?: boolean,
    onPlacesTenantSettingComplete?: PlacesTenantSettingComplete
): void {
    const oldOnPlacesTenantSettingComplete = onPlacesTenantSettingComplete;
    let shouldRunCompleteCallback = true;
    onPlacesTenantSettingComplete = (...args) => {
        if (shouldRunCompleteCallback) {
            shouldRunCompleteCallback = false;
            oldOnPlacesTenantSettingComplete?.(...args);
        }
    };

    if (!hasLoaded(mailboxInfo)) {
        const bootStep = startAppBootStep('PlacesSettings');
        const diagnostics: Record<string, string | boolean | number | undefined> = {};

        resetPlacesSettings(mailboxInfo);

        // Try to load offline settings first
        const offlinePromise = loadOfflinePlacesSettings(mailboxInfo);

        const onlinePromise = retryFetchPlacesSettings(mailboxInfo, shouldUseMainThread).then(
            (placesSettings): PlacesSettings => ({ ...placesSettings, isOffline: false })
        );

        new Promise<void>((resolve, reject) => {
            offlinePromise
                .then(placesSettings => {
                    diagnostics.hasOfflineCache = !!placesSettings;

                    if (!placesSettings) {
                        return;
                    }

                    // If the network call has already completed, we don't need to use the offline result.
                    if (hasLoaded(mailboxInfo)) {
                        diagnostics.fasterNetworkCallThanOffline = true;
                        return;
                    }

                    // We will only consider using the offline result if it indicates that the Places web app is enabled.
                    // Otherwise, we will wait for the online result.
                    if (!placesSettings.placesWebAppEnabled) {
                        diagnostics.ignoreOfflineResult = true;
                        return;
                    }

                    // Update store with offline settings
                    setPlacesSettings(mailboxInfo, placesSettings);

                    // Try to trigger the "complete" callback
                    resolve();
                })
                .catch(err => {
                    // Ignore the error in the offline call and continue the online call
                    diagnostics.hasOfflineError = true;
                    diagnostics.caughtOfflineError = typeof err === 'string' ? err : err?.message;
                });

            onlinePromise
                .then(placesSettings => {
                    // Update store with online settings
                    setPlacesSettings(mailboxInfo, placesSettings);

                    // Save to offline cache for future use
                    putOfflinePlacesSettings(mailboxInfo, placesSettings);

                    // Try to trigger the "complete" callback
                    resolve();
                })
                .catch(err => {
                    // Pass the error to the catch block
                    reject(err);
                });
        })
            .catch(async err => {
                bootStep.hasError = true;
                bootStep.caughtError = err;

                // Perhaps there is a parallel call that has completed and its result is already in use,
                // then we will ignore the error and continue to use the result.
                if (hasLoaded(mailboxInfo)) {
                    diagnostics.useResultFromAnotherCall = true;
                    return;
                }

                try {
                    // Try to use the offline cache even if it indicates that the Places web app is disabled
                    const placesSettings = await offlinePromise;

                    // Ignore the error if the offline cache was loaded successfully
                    if (placesSettings && !hasLoaded(mailboxInfo)) {
                        setPlacesSettings(mailboxInfo, placesSettings);
                        diagnostics.fallbackToOffline = true;
                        return;
                    }
                } catch {}

                // Fallback
                setPlacesSettingsFailed(mailboxInfo, {
                    ...defaultPlacesSettings,
                    reason: typeof err === 'string' ? err : err?.message,
                });
            })
            .finally(() => {
                // Call the complete callback
                const placesSettings = getPlacesSettings(mailboxInfo);
                onPlacesTenantSettingComplete?.(placesSettings);
                bootStep.end({
                    ...diagnostics,
                    isOffline: placesSettings.isOffline,
                    placesWebAppEnabled: placesSettings.placesWebAppEnabled,
                    placesPremiumEnabled: placesSettings.placesPremiumEnabled,
                    placesCopilotEnabled: placesSettings.placesCopilotEnabled,
                    placesFinderEnabled: placesSettings.placesFinderEnabled,
                    reason: placesSettings.reason,
                });
            });
    } else {
        // If the Places Settings have already been preloaded,
        // we manually trigger the "complete" callback to ensure that the caller is notified.
        const placesSettings = getPlacesSettings(mailboxInfo);
        onPlacesTenantSettingComplete?.(placesSettings);
    }
}

async function loadOfflinePlacesSettings(mailboxInfo: MailboxInfo) {
    try {
        const result = await getOfflinePlacesSettings(mailboxInfo);

        if (!result) {
            const err: TraceErrorObject = new Error('No offline Places Settings found');
            throw err;
        }

        const { timestamp } = result;
        const timestampDate = userDate(timestamp);
        const expirationDate = addDays(timestampDate, PLACES_SETTINGS_TTL_IN_DAYS);
        const nowDate = now();

        if (isAfter(nowDate, expirationDate)) {
            const err: TraceErrorObject = new Error('Offline Places Settings expired');
            err.additionalInfo = {
                timestamp: getISOString(timestampDate),
                expiration: getISOString(expirationDate),
                now: getISOString(nowDate),
            };
            throw err;
        }

        const placesSettings: PlacesSettings = {
            ...defaultPlacesSettings, // Ensure that all properties are present
            ...result.placesSettings,
            isOffline: true,
        };

        return placesSettings;
    } catch (err) {
        logGreyError(
            'LoadOfflinePlacesSettingsError',
            getModuleContextMailboxInfo(),
            err instanceof Error ? err : undefined
        );
        throw err;
    }
}
